import { useFrame, useThree } from '@react-three/fiber';
import { useMemo, useRef } from 'react';
import * as THREE from 'three';
import type { GeoNode } from '../location';
import { Dot } from './components/Dot';
import { MorphLines } from './components/MorphLines';
import {
  COASTLINE_COLOR,
  COASTLINE_OPACITY,
  RADIUS,
  ZONE_OPACITY,
} from './constants';
import { buildZoneLines, useCoastlines, zoneAt } from './geo';
import type { MorphRef, SceneChannels, SceneDriver } from './types';

const AXIS_X = new THREE.Vector3(1, 0, 0);
const AXIS_Y = new THREE.Vector3(0, 1, 0);

export function GlobeScene({
  node,
  driver,
}: {
  node: GeoNode;
  driver: SceneDriver;
}) {
  const groupRef = useRef<THREE.Group>(null);
  const backfaceMaskRef = useRef<THREE.Mesh>(null);
  const camera = useThree((s) => s.camera as THREE.OrthographicCamera);

  const coastlines = useCoastlines();
  const zoneLines = useMemo(
    () => buildZoneLines(zoneAt(node.lat, node.lon)),
    [node]
  );

  const channels = useMemo<SceneChannels>(
    () => ({
      morph: { current: 0 },
      coastlineOpacity: { current: 0 },
      timezoneOpacity: { current: 0 },
      dotReveal: { current: 0 } as MorphRef,
    }),
    []
  );

  const orientation = useMemo(() => new THREE.Quaternion(), []);
  const spin = useMemo(() => new THREE.Quaternion(), []);

  useFrame((state) => {
    const group = groupRef.current;
    if (!group) return;

    const { framing, scale, yaw, pitch } = driver({
      time: state.clock.elapsedTime,
      viewport: state.size,
      channels,
    });

    if (Math.abs(camera.zoom - framing.zoom) > 0.01) {
      camera.zoom = framing.zoom;
      camera.updateProjectionMatrix();
    }
    group.position.set(framing.x, framing.y, 0);
    group.scale.setScalar(scale);
    orientation
      .setFromAxisAngle(AXIS_X, pitch)
      .multiply(spin.setFromAxisAngle(AXIS_Y, yaw));
    group.quaternion.copy(orientation);

    backfaceMaskRef.current?.scale.setScalar(
      Math.max(0.0001, 1 - channels.morph.current * 3.5)
    );
  });

  return (
    <group ref={groupRef}>
      <mesh ref={backfaceMaskRef}>
        <sphereGeometry args={[RADIUS.backfaceMask, 48, 32]} />
        <meshBasicMaterial colorWrite={false} />
      </mesh>

      <MorphLines
        buffers={coastlines}
        color={COASTLINE_COLOR}
        opacity={COASTLINE_OPACITY}
        morphRef={channels.morph}
        opacityRef={channels.coastlineOpacity}
      />

      <MorphLines
        buffers={zoneLines}
        color={node.color}
        opacity={ZONE_OPACITY}
        morphRef={channels.morph}
        opacityRef={channels.timezoneOpacity}
      />

      <Dot
        node={node}
        morphRef={channels.morph}
        revealRef={channels.dotReveal}
      />
    </group>
  );
}
